<template>
  <section
    class="rounded-md border border-gray-200 bg-white"
    data-cy="job-cost-pricebook-time"
  >
    <header
      class="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold"
    >
      Pricebook vs. actual labor
      <Tooltip placement="top" :width="320">
        <template #trigger>
          <i
            class="fas fa-info-circle text-gray-500"
            tabindex="0"
            aria-label="About pricebook vs. actual labor"
            data-cy="job-cost-pricebook-time-info"
          ></i>
        </template>
        <template #tooltip>
          <span
            class="text-xs font-normal"
            data-cy="job-cost-pricebook-time-tooltip"
            >{{ PRICEBOOK_TIME_TOOLTIP }}</span
          >
        </template>
      </Tooltip>
    </header>
    <div
      class="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-200"
    >
      <div class="p-4">
        <div class="text-xs text-gray-600">Pricebook time</div>
        <div class="text-xl font-semibold" data-cy="job-cost-pricebook-minutes">
          <button
            v-if="jobCost.sold_minutes"
            type="button"
            class="border-0 bg-transparent p-0 text-blue-600 underline underline-offset-2 hover:text-blue-800"
            data-cy="job-cost-sold-time-link"
            title="Update each task's sold time on Job Times"
            @click="viewSoldTimes"
          >
            {{ soldLabel }}
          </button>
          <template v-else>{{ soldLabel }}</template>
        </div>
        <div class="text-xs text-gray-600">Time on the tasks sold</div>
      </div>
      <div class="p-4">
        <div class="text-xs text-gray-600">Time worked</div>
        <div class="text-xl font-semibold" data-cy="job-cost-worked-minutes">
          <JobCostValue v-if="timesInvalid" :value="null" not-available />
          <template v-else>{{ formatDuration(workedMinutes) }}</template>
        </div>
        <div class="text-xs text-gray-600">{{ peopleLabel }}, all visits</div>
      </div>
      <div class="p-4 bg-gray-50">
        <div class="text-xs text-gray-600">Difference</div>
        <div
          class="text-xl font-semibold"
          :class="difference?.colorClass"
          data-cy="job-cost-time-difference"
        >
          <template v-if="difference">{{ difference.headline }}</template>
          <JobCostValue v-else :value="null" :not-available="timesInvalid" />
        </div>
      </div>
    </div>
    <div
      class="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-200 border-t border-gray-200"
      data-cy="job-cost-labor-dollars"
    >
      <div class="p-4">
        <div class="text-xs text-gray-600">Pricebook labor</div>
        <div class="text-xl font-semibold" data-cy="job-cost-sold-labor">
          <JobCostValue :value="jobCost.sold_labor_cost_cents" />
        </div>
        <div class="text-xs text-gray-600">
          Pricebook time at {{ dollars(jobCost.department_rate_cents) }}/hr
        </div>
      </div>
      <div class="p-4">
        <div class="text-xs text-gray-600">Actual labor</div>
        <div class="text-xl font-semibold" data-cy="job-cost-actual-labor">
          <JobCostValue
            :value="jobCost.labor_cost_cents"
            :not-available="timesInvalid"
          />
        </div>
        <div class="text-xs text-gray-600">
          Time worked at each person's rate
        </div>
      </div>
      <div class="p-4 bg-gray-50">
        <div class="text-xs text-gray-600">Difference</div>
        <div
          class="text-xl font-semibold"
          :class="laborDifference?.colorClass"
          data-cy="job-cost-labor-vs-sold"
        >
          <template v-if="laborDifference">{{
            laborDifference.headline
          }}</template>
          <JobCostValue v-else :value="null" :not-available="timesInvalid" />
        </div>
        <div
          v-if="laborDifference?.note"
          class="text-xs text-gray-600"
          data-cy="job-cost-labor-vs-sold-note"
        >
          {{ laborDifference.note }}
        </div>
      </div>
    </div>
    <p
      v-if="rateNote"
      class="border-t border-gray-200 bg-gray-50 px-4 py-2 text-xs text-gray-700"
      data-cy="job-cost-rate-note"
    >
      <i class="fas fa-info-circle mr-1 text-gray-500" aria-hidden="true"></i
      >{{ rateNote }}
    </p>
    <footer
      v-if="difference?.cta"
      class="flex flex-col md:flex-row md:items-center justify-between gap-3 border-t border-gray-200 px-4 py-3 text-sm"
      data-cy="job-cost-pricebook-cta"
    >
      <span>{{ difference.cta }}</span>
      <a
        v-if="departmentId"
        :href="`/departments/${departmentId}/pricebook`"
        class="flex shrink-0 justify-center items-center px-5 py-2.5 !rounded-lg border text-gray-700 bg-white border-gray-300 hover:bg-gray-100 no-underline"
        data-cy="job-cost-pricebook-link"
        >Review pricebook times</a
      >
    </footer>
  </section>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { EventBus } from "@/lib/event_bus.js";

import Tooltip from "@/components/Tooltip.vue";
import { dollars, formatDuration } from "@/mixins";
import type { JobCost } from "@/typings/jobCost";
import JobCostValue from "./JobCostValue.vue";

// Time first, since the pricebook sets it; dollars below, with a note when the people's rates flip the two.
const props = defineProps<{
  jobCost: JobCost;
  workedMinutes: number;
  peopleLabel: string;
  timesInvalid: boolean;
  departmentId?: number;
}>();

const PRICEBOOK_TIME_TOOLTIP =
  "Pricebook time is the time on the pricebook tasks sold on this job. Time worked is pre-work, drive and on-site time from completed visits, added up for everyone on the job (the same as Job Efficiency). Pricebook labor prices the pricebook time at the department's labor rate; actual labor prices each person's time at their own rate. If jobs like this keep running over or under, change the task's time or price in the pricebook.";

// Sold time is per task, so its editor lives in Job Times' Sold Time table.
function viewSoldTimes(): void {
  EventBus.$emit("set-selected-tab", "Job Times");
}

const soldLabel = computed(() =>
  props.jobCost.sold_minutes === null
    ? "--"
    : formatDuration(props.jobCost.sold_minutes)
);

const difference = computed(() => {
  const minutes = props.jobCost.time_vs_sold_minutes;
  if (minutes === null) return null;
  if (minutes === 0) return { headline: "On target", colorClass: "", cta: "" };
  const over = minutes > 0;
  return {
    headline: `${formatDuration(Math.abs(minutes))} ${over ? "over" : "under"}`,
    cta: over
      ? "If jobs like this often run long, raise the task's time or price in the pricebook."
      : "If jobs like this often finish early, the pricebook time may be padded.",
    colorClass: over ? "text-red-600" : "text-green-700"
  };
});

const laborDifference = computed(() => {
  const cents = props.jobCost.labor_vs_sold_cents;
  if (cents === null) return null;
  if (cents === 0) {
    return { headline: "On target", colorClass: "", note: "" };
  }
  const over = cents > 0;
  return {
    headline: `${dollars(Math.abs(cents))} ${over ? "over" : "under"}`,
    note: `${over ? "more" : "less"} labor cost than the price assumed`,
    colorClass: over ? "text-red-600" : "text-green-700"
  };
});

// Time and dollars disagree only through the people's rates against the department rate.
const rateNote = computed(() => {
  const minutes = props.jobCost.time_vs_sold_minutes;
  const cents = props.jobCost.labor_vs_sold_cents;
  if (minutes === null || cents === null) return "";
  const rate = `the department's ${dollars(props.jobCost.department_rate_cents)}/hr`;
  if (minutes > 0 && cents <= 0) {
    return `The job ran long, but the people on it cost less per hour than ${rate}, so labor cost still came in ${cents === 0 ? "on target" : "under"}.`;
  }
  if (minutes < 0 && cents >= 0) {
    return `The job finished early, but the people on it cost more per hour than ${rate}, so labor cost still came in ${cents === 0 ? "on target" : "over"}.`;
  }
  if (minutes === 0 && cents !== 0) {
    return `The job took its pricebook time. The dollar difference comes from the people's rates against ${rate}.`;
  }
  return "";
});
</script>
